import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faPrint, faPlus, faMinus, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import PatternLock from '../shared/PatternLock';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import enUS from 'date-fns/locale/en-US';
import itIT from 'date-fns/locale/it';
import zhCN from 'date-fns/locale/zh-CN';
import esES from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';

// Registra le localizzazioni
registerLocale('en', enUS);
registerLocale('it', itIT);
registerLocale('zh', zhCN);
registerLocale('es', esES);
import './DeviceForm.scss';
import { formatDate, parseDate } from '../../utils/dateUtils';
import { printReceipt } from '../../services/printService';
import Receipt from '../shared/Receipt';
import settingsService from '../../services/settingsService';
import { inventoryService } from '../../services/api';
import { cacheService } from '../../services/cacheService';
import { retryWithBackoff } from '../../utils/retryUtils';
import { auth } from '../../services/firebase';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../contexts/SubscriptionContext';

function DeviceForm({ show, device, onHide, onSave, initialReceiptNumber, initialValues }) {
  const { t, i18n } = useTranslation();
  const { 
    canCreateDevice, 
    incrementDeviceCount, 
    subscription, 
    loading: subscriptionLoading,
    getRemainingDevices
  } = useSubscription();
  
  const [formData, setFormData] = useState(device || {
    brand: '',
    model: '',
    serialNumber: '',
    customerPhone: '',
    problems: [''],
    customProblems: [],
    notes: '',
    price: '',
    deposit: '',
    paymentStatus: 'nonPagato',
    isOrder: false,
    pattern: [],
    passcode: '',
    unlockType: 'pattern',
    deliveryDate: null,
    status: 'inRiparazione'
  });

  const [repairNumber, setRepairNumber] = useState(initialReceiptNumber);
  const [errors, setErrors] = useState({});
  const [autoPrint, setAutoPrint] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isSaving, setSaving] = useState(false);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [currentReceiptNumber, setCurrentReceiptNumber] = useState(null);
  const [error, setError] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  // Aggiorna il DatePicker per utilizzare la lingua corrente
  const currentLocale = (() => {
    switch (i18n.language) {
      case 'it': return itIT;
      case 'zh': return zhCN;
      case 'es': return esES;
      default: return enUS;
    }
  })();

  useEffect(() => {
    if (show) {
      if (device) {
        const deviceWithCustom = {...device};
        if (!deviceWithCustom.customProblems) {
          deviceWithCustom.customProblems = Array(deviceWithCustom.problems?.length || 0).fill('');
        }
        setFormData(deviceWithCustom);
        setRepairNumber(device.repairNumber);
        setSubscriptionError(null);
      } else if (initialValues) {
        setFormData({
          brand: initialValues.brand || '',
          model: initialValues.model || '',
          serialNumber: '',
          customerPhone: '',
          problems: initialValues.problems?.length ? initialValues.problems : [''],
          customProblems: initialValues.customProblems?.length ? initialValues.customProblems : [''],
          notes: '',
          price: initialValues.price || '',
          deposit: '',
          paymentStatus: 'nonPagato',
          isOrder: false,
          pattern: [],
          passcode: '',
          unlockType: 'pattern',
          deliveryDate: null,
          status: 'inRiparazione'
        });
        setRepairNumber(initialReceiptNumber);

        checkSubscriptionStatus();
      } else {
        setFormData({
          brand: '',
          model: '',
          serialNumber: '',
          customerPhone: '',
          problems: [''],
          customProblems: [''],
          notes: '',
          price: '',
          deposit: '',
          paymentStatus: 'nonPagato',
          isOrder: false,
          pattern: [],
          passcode: '',
          unlockType: 'pattern',
          deliveryDate: null,
          status: 'inRiparazione'
        });
        setRepairNumber(initialReceiptNumber);
        
        checkSubscriptionStatus();
      }
    }
  }, [show, device, initialValues, initialReceiptNumber]);

  const checkSubscriptionStatus = async () => {
    if (!device) {  // Solo per nuovi dispositivi
      try {
        const result = await canCreateDevice();
        
        if (!result.canCreate) {
          setSubscriptionError(result.reason);
        } else {
          setSubscriptionError(null);
        }
      } catch (error) {
        console.error('Errore nella verifica dell\'abbonamento:', error);
        setSubscriptionError(t('settings.subscription.errors.verificationFailed'));
      }
    }
  };

  useEffect(() => {
    const fetchCurrentReceiptNumber = async () => {
      try {
        if (isOffline) {
          const cachedSettings = await cacheService.get('offline_settings');
          const cachedNumber = cachedSettings?.currentReceiptNumber || initialReceiptNumber || 1;
          setCurrentReceiptNumber(cachedNumber);
        } else {
          const { data: settings } = await settingsService.get();
          
          const maxRepairNumber = await settingsService.getMaxRepairNumber();
          
          const nextNumber = Math.max(
            maxRepairNumber + 1,
            settings.startingReceiptNumber || 1,
            initialReceiptNumber || 1
          );
          
          setCurrentReceiptNumber(nextNumber);
          
          if (nextNumber !== settings.currentReceiptNumber) {
            await settingsService.update({
              ...settings,
              currentReceiptNumber: nextNumber
            });
          }
        }
      } catch (error) {
        console.error('Error fetching receipt number:', error);
        setCurrentReceiptNumber(Math.max(initialReceiptNumber || 1, 1));
      }
    };

    if (show && !device) {
      fetchCurrentReceiptNumber();
    }
  }, [show, device, initialReceiptNumber, isOffline]);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      await syncReceiptNumbers();
      await syncPendingSubmissions();
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const syncPendingSubmissions = async () => {
    const submissions = await cacheService.get('pending_device_submissions') || [];
    
    for (const submission of submissions) {
      try {
        await retryWithBackoff(() => onSave(submission));
        const remainingSubmissions = submissions.filter(s => 
          s.tempId !== submission.tempId
        );
        await cacheService.set('pending_device_submissions', remainingSubmissions);
        setPendingSubmissions(remainingSubmissions);
      } catch (error) {
        console.error('Error syncing submission:', error);
      }
    }
  };

  const syncReceiptNumbers = async () => {
    try {
      const cachedSettings = await cacheService.get('offline_settings');
      if (cachedSettings?.currentReceiptNumber) {
        const { data: serverSettings } = await settingsService.get();
        if (cachedSettings.currentReceiptNumber > serverSettings.currentReceiptNumber) {
          await settingsService.update({
            ...serverSettings,
            currentReceiptNumber: cachedSettings.currentReceiptNumber
          });
        }
        await cacheService.remove('offline_settings');
      }
    } catch (error) {
      console.error('Error syncing receipt numbers:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('problem-')) {
      const index = parseInt(name.split('-')[1]);
      const newProblems = [...formData.problems];
      newProblems[index] = value;
      
      setFormData(prev => ({
        ...prev,
        problems: newProblems
      }));
    } else if (name.startsWith('customProblem-')) {
      const index = parseInt(name.split('-')[1]);
      const newCustomProblems = [...(formData.customProblems || Array(formData.problems.length).fill(''))];
      newCustomProblems[index] = value;
      
      setFormData(prev => ({
        ...prev,
        customProblems: newCustomProblems
      }));
    } else if (name === 'deposit') {
      const depositValue = parseFloat(value);
      
      setFormData(prev => ({
        ...prev,
        deposit: value,
        paymentStatus: depositValue > 0 && prev.paymentStatus === 'nonPagato' ? 'acconto' : prev.paymentStatus
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
        ...(name === 'isOrder' && {
          status: checked ? 'ordine' : 'inRiparazione'
        })
      }));
    }
  };

  const handleAddProblem = () => {
    setFormData(prev => ({
      ...prev,
      problems: [...prev.problems, ''],
      customProblems: [...(prev.customProblems || []), '']
    }));
  };

  const handleRemoveProblem = (index) => {
    setFormData(prev => ({
      ...prev,
      problems: prev.problems.filter((_, i) => i !== index),
      customProblems: (prev.customProblems || []).filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.brand?.trim()) {
      newErrors.brand = 'La marca è obbligatoria';
    }
    
    if (!formData.model?.trim()) {
      newErrors.model = 'Il modello è obbligatorio';
    }
    
    if (!formData.problems.some(p => p.trim())) {
      newErrors.problems = 'Almeno un problema è obbligatorio';
    }
    
    if (formData.price && formData.price < 0) {
      newErrors.price = 'Il prezzo non può essere negativo';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || isSaving) {
      return;
    }

    if (!device) {
      try {
        const result = await canCreateDevice();
        
        if (!result.canCreate) {
          setSubscriptionError(result.reason);
          return;
        }
      } catch (error) {
        console.error('Errore nella verifica dell\'abbonamento:', error);
        setSubscriptionError(t('settings.subscription.errors.verificationFailed'));
        return;
      }
    }

    try {
      setSaving(true);

      const deviceData = {
        ...formData,
        repairNumber: device ? device.repairNumber : currentReceiptNumber,
        price: formData.price ? Number(formData.price) : 0,
        deposit: formData.deposit ? Number(formData.deposit) : 0,
        deliveryDate: formData.deliveryDate ? new Date(formData.deliveryDate) : null,
        entryDate: device?.entryDate || new Date(),
        status: formData.isOrder ? 'ordine' : 'inRiparazione',
        pattern: formData.pattern || [],
        passcode: formData.passcode || '',
        unlockType: formData.unlockType || 'pattern',
        problems: formData.problems.map((problem, index) => {
          if (problem === 'Altro' && formData.customProblems && formData.customProblems[index]) {
            return 'Altro:' + formData.customProblems[index];
          }
          return problem;
        }).filter(p => p.trim())
      };

      await onSave(deviceData);

      if (!device) {
        await incrementDeviceCount();
      }

      const { data: companyData } = await settingsService.get();

      if (companyData.autoPrint) {
        try {
          await printReceipt(deviceData, companyData);
        } catch (printError) {
          console.error('Errore durante la stampa:', printError);
          alert('Errore durante la stampa della ricevuta. La riparazione è stata salvata comunque.');
        }
      }

      if (!device) {
        try {
          await settingsService.update({
            ...companyData,
            currentReceiptNumber: Math.max(
              currentReceiptNumber,
              companyData.startingReceiptNumber || 1
            )
          });
        } catch (error) {
          console.error('Error updating receipt number:', error);
        }
      }

      onHide();
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      alert('Si è verificato un errore durante il salvataggio. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      deliveryDate: date
    }));
  };

  const handleTimeChange = (value, type) => {
    setFormData(prev => {
      if (!prev.deliveryDate) return prev;
      
      const newDate = new Date(prev.deliveryDate);
      
      if (type === 'hours') {
        newDate.setHours(parseInt(value));
      } else if (type === 'minutes') {
        newDate.setMinutes(parseInt(value));
      }
      
      return {
        ...prev,
        deliveryDate: newDate
      };
    });
  };

  const handlePrint = async (deviceData) => {
    try {
      const { data: companyData } = await settingsService.get();
      
      const printData = {
        ...deviceData,
        problems: Array.isArray(deviceData.problems) 
          ? deviceData.problems.filter(p => p.trim())
          : [deviceData.problems].filter(p => p && p.trim()),
        entryDate: deviceData.entryDate || new Date(),
        price: parseFloat(deviceData.price || 0),
        deposit: parseFloat(deviceData.deposit || 0)
      };

      await printReceipt(printData, companyData);
    } catch (error) {
      console.error('Errore durante la stampa:', error);
      if (error.message === 'Popup bloccato dal browser') {
        alert(t('deviceForm.errors.print.popup'));
      } else {
        alert(t('deviceForm.errors.print.generic'));
      }
    }
  };

  const handlePatternChange = (newPattern) => {
    console.log('Pattern changed:', newPattern);
    setFormData(prev => ({
      ...prev,
      pattern: newPattern
    }));
  };

  const PROBLEM_OPTIONS = [
    { value: 'Schermo', label: t('models.device.problems.Schermo') },
    { value: 'Batteria', label: t('models.device.problems.Batteria') },
    { value: 'Software', label: t('models.device.problems.Software') },
    { value: 'Connettore', label: t('models.device.problems.Connettore') },
    { value: 'Fotocamera', label: t('models.device.problems.Fotocamera') },
    { value: 'Autoparlante', label: t('models.device.problems.Autoparlante') },
    { value: 'Cassa', label: t('models.device.problems.Cassa') },
    { value: 'Microfono', label: t('models.device.problems.Microfono') },
    { value: 'Acqua', label: t('models.device.problems.Acqua') },
    { value: 'NonSiAccende', label: t('models.device.problems.NonSiAccende') },
    { value: 'Reset', label: t('models.device.problems.Reset') },
    { value: 'Altro', label: t('models.device.problems.Altro') }
  ];

  const remainingDevices = getRemainingDevices();

  return (
    <Modal show={show} onHide={onHide} size="lg" className="device-form-modal">
      {isOffline && (
        <div className="alert alert-warning m-3 mb-0 rounded-0">
          {t('deviceForm.offline.message')}
          {pendingSubmissions.length > 0 && (
            <div className="mt-2">
              {t('deviceForm.offline.pendingDevices', { count: pendingSubmissions.length })}
            </div>
          )}
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {!device && subscription && subscription.plan === 'free' && !subscriptionError && (
        <Alert variant="info" className="m-3 mb-0">
          <div className="d-flex align-items-center">
            <div>
              <strong>{t('settings.subscription.freePlan.title')}:</strong> {t('settings.subscription.freePlan.deviceUsage', { remaining: remainingDevices, total: subscription.deviceLimit })}
            </div>
          </div>
        </Alert>
      )}

      {subscriptionError && (
        <Alert variant="danger" className="m-3 mb-0">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <div>
              <strong>{t('settings.subscription.errors.title')}:</strong> {subscriptionError}
            </div>
          </div>
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            {device ? 
              `${t('deviceForm.title.edit')} #${device.repairNumber}` : 
              `${t('deviceForm.title.new')} #${currentReceiptNumber || '...'}`
            }
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          <Row className="g-4">
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.brand')}</Form.Label>
                <Form.Control
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  required
                  className="border"
                  disabled={subscriptionError}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.model')}</Form.Label>
                <Form.Control
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  className="border"
                  disabled={subscriptionError}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.serialNumber')}</Form.Label>
                <Form.Control
                  type="text"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  className="border"
                  disabled={subscriptionError}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.customerPhone')}</Form.Label>
                <Form.Control
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleChange}
                  className="border"
                  disabled={subscriptionError}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.problems')}</Form.Label>
                {formData.problems.map((problem, index) => (
                  <div key={index} className="mb-2">
                    <div className="d-flex mb-2">
                      <Form.Select
                        name={`problem-${index}`}
                        value={problem}
                        onChange={handleChange}
                        className="form-control me-2"
                        disabled={subscriptionError}
                      >
                        <option value="">{t('deviceForm.fields.selectProblem')}</option>
                        {PROBLEM_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                      {index > 0 && (
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleRemoveProblem(index)}
                          className="flex-shrink-0"
                          disabled={subscriptionError}
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </Button>
                      )}
                    </div>
                    {problem === 'Altro' && (
                      <Form.Control
                        type="text"
                        name={`customProblem-${index}`}
                        value={(formData.customProblems || [])[index] || ''}
                        onChange={handleChange}
                        placeholder={t('deviceForm.fields.customProblem') || "Specifica il problema"}
                        className="mb-2 border"
                        disabled={subscriptionError}
                      />
                    )}
                  </div>
                ))}
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={handleAddProblem}
                  className="mt-2"
                  disabled={subscriptionError}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  {t('deviceForm.fields.addProblem')}
                </Button>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.notes')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="border"
                  disabled={subscriptionError}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.price')}</Form.Label>
                <Form.Control
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  required
                  className="border"
                  disabled={subscriptionError}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.deposit')}</Form.Label>
                <Form.Control
                  type="number"
                  name="deposit"
                  value={formData.deposit}
                  onChange={handleChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="border"
                  disabled={subscriptionError}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.deliveryDate')}</Form.Label>
                <div className="date-time-select-container">
                  <div className="date-picker-container">
                    <DatePicker
                      selected={formData.deliveryDate}
                      onChange={handleDateChange}
                      dateFormat="dd/MM/yyyy"
                      className="form-control border"
                      placeholderText={t('deviceForm.fields.selectDate')}
                      locale={currentLocale}
                      disabled={subscriptionError}
                      popperClassName="datepicker-improved"
                    />
                  </div>
                  {formData.deliveryDate && (
                    <div className="time-select-container mt-2">
                      <Form.Label>{t('deviceForm.fields.deliveryTime', 'Ora di consegna')}</Form.Label>
                      <div className="d-flex">
                        <Form.Select
                          value={formData.deliveryDate ? formData.deliveryDate.getHours() : ""}
                          onChange={(e) => handleTimeChange(e.target.value, 'hours')}
                          disabled={subscriptionError}
                          className="time-select me-2"
                        >
                          <option value="" disabled>{t('deviceForm.fields.hour', 'Ora')}</option>
                          {Array.from({ length: 24 }).map((_, i) => (
                            <option key={`hour-${i}`} value={i}>
                              {i.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </Form.Select>
                        
                        <Form.Select
                          value={formData.deliveryDate ? formData.deliveryDate.getMinutes() : ""}
                          onChange={(e) => handleTimeChange(e.target.value, 'minutes')}
                          disabled={subscriptionError}
                          className="time-select"
                        >
                          <option value="" disabled>{t('deviceForm.fields.minute', 'Minuti')}</option>
                          {[0, 15, 30, 45].map((minutes) => (
                            <option key={`min-${minutes}`} value={minutes}>
                              {minutes.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>
                  )}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('deviceForm.fields.paymentStatus.label')}</Form.Label>
                <Form.Select
                  name="paymentStatus"
                  value={formData.paymentStatus}
                  onChange={handleChange}
                  className="border"
                  disabled={subscriptionError}
                >
                  <option value="nonPagato">{t('deviceForm.fields.paymentStatus.notPaid')}</option>
                  <option value="acconto">{t('deviceForm.fields.paymentStatus.deposit')}</option>
                  <option value="pagato">{t('deviceForm.fields.paymentStatus.paid')}</option>
                  <option value="nonRichiesto">{t('deviceForm.fields.paymentStatus.notRequired')}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={t('deviceForm.fields.isOrder')}
                  name="isOrder"
                  checked={formData.isOrder}
                  onChange={handleChange}
                  disabled={subscriptionError}
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">{t('deviceForm.fields.unlockPattern.label', 'Codice di Sblocco')}</Form.Label>
                  <Form.Check
                    type="switch"
                    id="unlock-type-switch"
                    label={formData.unlockType === 'passcode' ? t('deviceForm.fields.unlockPattern.passcode', 'Codice') : t('deviceForm.fields.unlockPattern.pattern', 'Pattern')}
                    checked={formData.unlockType === 'passcode'}
                    onChange={() => {
                      setFormData(prev => ({
                        ...prev,
                        unlockType: prev.unlockType === 'passcode' ? 'pattern' : 'passcode',
                        pattern: prev.unlockType === 'passcode' ? prev.pattern : [],
                        passcode: prev.unlockType === 'pattern' ? prev.passcode : ''
                      }));
                    }}
                    disabled={subscriptionError}
                  />
                </div>
                
                {formData.unlockType === 'passcode' ? (
                  <Form.Control
                    type="text"
                    name="passcode"
                    value={formData.passcode || ''}
                    onChange={handleChange}
                    placeholder={t('deviceForm.fields.unlockPattern.enterPasscode', 'Inserisci il codice di sblocco')}
                    className="border mb-2"
                    disabled={subscriptionError}
                  />
                ) : (
                  <>
                    <PatternLock
                      onComplete={handlePatternChange}
                      initialPattern={formData.pattern}
                      disabled={subscriptionError}
                    />
                    {formData.pattern?.length > 0 && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="mt-2"
                        onClick={() => handlePatternChange([])}
                        disabled={subscriptionError}
                      >
                        {t('deviceForm.fields.unlockPattern.reset')}
                      </Button>
                    )}
                  </>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="secondary" onClick={onHide}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {t('deviceForm.buttons.cancel')}
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={
              !formData.brand || 
              !formData.model || 
              !formData.problems.some(p => p.trim()) || 
              isSaving || 
              subscriptionError
            }
          >
            <FontAwesomeIcon 
              icon={isSaving ? faSpinner : faSave} 
              className="me-2" 
              spin={isSaving} 
            />
            {isSaving ? t('deviceForm.buttons.saving') : 
             t('deviceForm.buttons.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default DeviceForm; 